import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { fmtFecha, ymdMadrid } from "@/lib/fechas";

/**
 * Timeline visual (SVG) del histórico de DeCA por día.
 * Cada punto contiene generado/modificado/anulado apilados y total.
 */
function puntosFromItems(items) {
  const map = new Map();
  for (const d of items || []) {
    const fecha = ymdMadrid(d.created_at);
    if (!fecha) continue;
    const b = map.get(fecha) || { fecha, generado: 0, modificado: 0, anulado: 0, envios: 0, total: 0 };
    const est = d.estado || "generado";
    if (est === "anulado") b.anulado += 1;
    else if (est === "modificado") b.modificado += 1;
    else b.generado += 1;
    b.envios += Number(d.num_envios) || 0;
    b.total = b.generado + b.modificado + b.anulado;
    map.set(fecha, b);
  }
  return [...map.values()].sort((a, c) => a.fecha.localeCompare(c.fecha));
}

export default function DecaTimeline({ items, desde, hasta, estado, cargador, transportista, buscar }) {
  const [puntos, setPuntos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (items) {
      setPuntos(puntosFromItems(items));
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (desde) params.desde = desde;
        if (hasta) params.hasta = hasta;
        if (estado) params.estado = estado;
        if (cargador) params.cargador = cargador;
        if (transportista) params.transportista = transportista;
        if (buscar) params.buscar = buscar;
        const { data } = await api.get("/api/v1/aggregates/timeline", { params });
        setPuntos(data.puntos || []);
      } catch (_e) { setPuntos([]); }
      finally { setLoading(false); }
    })();
  }, [items, desde, hasta, estado, cargador, transportista, buscar]);

  if (loading) return (
    <Card className="p-8 flex items-center justify-center text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2"/> Cargando timeline…
    </Card>
  );

  if (puntos.length === 0) return (
    <Card className="p-8 text-center text-slate-500">Sin datos para la línea temporal.</Card>
  );

  const maxTotal = Math.max(...puntos.map((p) => p.total), 1);
  const width = 900;
  const height = 260;
  const padL = 40, padR = 20, padT = 20, padB = 40;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;
  const stepX = puntos.length > 1 ? chartW / (puntos.length - 1) : 0;
  const yFor = (v) => padT + chartH - (v / maxTotal) * chartH;

  const pointsGen = puntos.map((p, i) => `${padL + i * stepX},${yFor(p.generado)}`).join(" ");
  const pointsMod = puntos.map((p, i) => `${padL + i * stepX},${yFor(p.modificado)}`).join(" ");
  const pointsAnn = puntos.map((p, i) => `${padL + i * stepX},${yFor(p.anulado)}`).join(" ");
  const totals = puntos.reduce((a, p) => ({
    gen: a.gen + p.generado, mod: a.mod + p.modificado, ann: a.ann + p.anulado, env: a.env + p.envios,
  }), { gen: 0, mod: 0, ann: 0, env: 0 });

  return (
    <Card className="p-4" data-testid="timeline-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="label-tag">Actividad diaria</div>
          <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {puntos.length} día{puntos.length === 1 ? "" : "s"} con actividad · {totals.gen + totals.mod + totals.ann} DeCA · {totals.env} envíos
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500"/> Generados <span className="font-semibold">{totals.gen}</span></div>
          <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500"/> Modificados <span className="font-semibold">{totals.mod}</span></div>
          <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500"/> Anulados <span className="font-semibold">{totals.ann}</span></div>
        </div>
      </div>

      <div className="relative overflow-x-auto">
        <svg width={width} height={height} className="text-slate-400">
          {/* Ejes */}
          {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
            <g key={i}>
              <line x1={padL} x2={width - padR} y1={padT + chartH * (1 - t)} y2={padT + chartH * (1 - t)} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="3 3"/>
              <text x={padL - 6} y={padT + chartH * (1 - t) + 4} textAnchor="end" fontSize="10" fill="currentColor">{Math.round(maxTotal * t)}</text>
            </g>
          ))}
          {/* Líneas */}
          <polyline points={pointsGen} fill="none" stroke="#10b981" strokeWidth="2" strokeLinejoin="round"/>
          <polyline points={pointsMod} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinejoin="round"/>
          <polyline points={pointsAnn} fill="none" stroke="#ef4444" strokeWidth="2" strokeLinejoin="round"/>
          {/* Puntos + labels */}
          {puntos.map((p, i) => {
            const x = padL + i * stepX;
            return (
              <g key={p.fecha} onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}>
                <circle cx={x} cy={yFor(p.total)} r={4} fill="#0f172a" data-testid={`timeline-point-${i}`}/>
                {puntos.length <= 30 ? (
                  <text x={x} y={height - padB + 14} textAnchor="middle" fontSize="9" fill="#64748b" transform={`rotate(-35 ${x} ${height - padB + 14})`}>
                    {fmtFecha(p.fecha).slice(0, 5)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      {hover ? (
        <div className="mt-3 border border-slate-200 rounded-md p-3 bg-slate-50 flex flex-wrap gap-4 text-sm" data-testid="timeline-hover">
          <div><b>{fmtFecha(hover.fecha)}</b></div>
          <div>Generados: <b className="text-emerald-700">{hover.generado}</b></div>
          <div>Modificados: <b className="text-amber-700">{hover.modificado}</b></div>
          <div>Anulados: <b className="text-red-700">{hover.anulado}</b></div>
          <div>Envíos: <b>{hover.envios}</b></div>
        </div>
      ) : null}
    </Card>
  );
}
